// playerListComponents/PlayerResults.js    
import React, { useState, useEffect } from 'react';
import { 
  Box, TableContainer, Paper, Table, TableHead, TableRow, TableCell, 
  TableBody, Avatar, Typography, Checkbox, IconButton, TablePagination, Chip, 
  Card, CardHeader, CardContent, CardActions, Grid, Tooltip, FormControlLabel, Button,
  Dialog, DialogTitle, DialogContent, List, ListItem, ListItemText, Divider,
  ListItemAvatar, DialogActions, TextField
} from '@mui/material';
import { 
  Visibility, Delete as DeleteIcon, Star as StarIcon, Visibility as VisibilityIcon, 
  Assignment as AssignmentIcon, Lightbulb as LightbulbIcon, Description as DescriptionIcon, 
  VideoLibrary as VideoLibraryIcon, Healing as HealingIcon, Gavel as GavelIcon, TrendingUp, TrendingFlat, TrendingDown,
  Refresh as RefreshIcon, Cancel as CancelIcon, RemoveRedEye as MonitorIcon, HelpOutline as HelpOutlineIcon,
  Straighten as StraightenIcon,
  FolderOpen as FolderOpenIcon,
  Sports as SportsIcon, Height as HeightIcon, Flag as FlagIcon, Group as GroupIcon,
  Assessment as AssessmentIcon, SportsSoccer as TeamIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import FlagDisplay from '../components/FlagDisplay';
import { usePlayerComparison } from '../context/PlayerComparisonContext';
import { useClubTheme } from '../contexts/ClubThemeContext';

const calculateDecimalAge = (dateNaissance) => {
  if (!dateNaissance) return '';
  const birthDate = new Date(dateNaissance);
  const today = new Date();
  let years = today.getFullYear() - birthDate.getFullYear();

  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    years--;
  }

  const diffTime = Math.abs(today - birthDate);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  const decimal = (diffDays % 365.25) / 365.25;

  return (years + decimal).toFixed(1);
};

// Convertir cm en pieds et pouces (format américain)
const formatHeight = (heightCm, language) => {
  if (!heightCm) return '';
  if (language === 'en') {
    const totalInches = heightCm / 2.54;
    const feet = Math.floor(totalInches / 12);
    const inches = Math.round(totalInches % 12);
    return `${feet}'${inches}"`;
  }
  return `${heightCm}cm`;
};

const getContrastColor = (hexcolor) => {
  if (!hexcolor) return '#000000';
  const r = parseInt(hexcolor.slice(1, 3), 16);
  const g = parseInt(hexcolor.slice(3, 5), 16);
  const b = parseInt(hexcolor.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
};

const badgeStyle = (bgColor) => ({
  position: 'relative',
  width: 24,
  height: 24,
  borderRadius: '50%',
  backgroundColor: bgColor,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  fontSize: '14px',
  marginLeft: '-4px',
  border: '1px solid #fff',
});

const FormaIndicator = ({ forma }) => {
  if (!forma) return null;
  
  switch (forma.toLowerCase()) {
    case 'high':
      return <TrendingUp sx={{ color: '#4caf50', fontSize: 24 }} />;
    case 'stable':
      return <TrendingFlat sx={{ color: '#ff9800', fontSize: 24 }} />;
    case 'low':
      return <TrendingDown sx={{ color: '#f44336', fontSize: 24 }} />;
    default:
      return null;
  }
};

const CountBadge = ({ count, label }) => {
  const backgroundColor = count <= 1 ? '#f44336' : '#4caf50';
  const textColor = '#ffffff';
  
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        backgroundColor: backgroundColor,
        color: textColor,
        width: '28px',
        height: '28px',
        fontSize: '12px',
        fontWeight: 'bold',
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
        border: '2px solid #fff',
      }}
    >
      {count}
    </Box>
  );
};

// Composant pour afficher les verdicts scouts
const VerdictIndicators = ({ player, isMobile = false, userRole, currentUserId }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedVerdict, setSelectedVerdict] = useState(null);
  const { t } = useTranslation();

  const verdictConfig = {
    'VOLVER A VER': { icon: <RefreshIcon fontSize="small" />, color: '#2196f3', emoji: '🔄', label: t('playerFilters.verdictOptions.watchAgain') },
    'DESCARTADO': { icon: <CancelIcon fontSize="small" />, color: '#f44336', emoji: '❌', label: t('playerFilters.verdictOptions.discarded') },
    'MONITORIZAR': { icon: <MonitorIcon fontSize="small" />, color: '#ff9800', emoji: '👁️', label: t('playerFilters.verdictOptions.monitor') },
    'PRIORITARIO': { icon: <StarIcon fontSize="small" />, color: '#4caf50', emoji: '⭐', label: t('playerFilters.verdictOptions.priority') },
    'DUDAS': { icon: <HelpOutlineIcon fontSize="small" />, color: '#9c27b0', emoji: '❓', label: t('playerFilters.verdictOptions.dudas') }
  };

  if (!player.scout_verdicts || player.scout_verdicts.length === 0) return null;

  // Filtrer les verdicts selon le rôle
  let filteredVerdicts = player.scout_verdicts;
  if (userRole === 'entrenador') {
    // Pour les entrenadores, montrer seulement leurs propres verdicts
    filteredVerdicts = player.scout_verdicts.filter(verdict => verdict.user_id === currentUserId);
    if (filteredVerdicts.length === 0) return null;
  }

  // Grouper les verdicts par type
  const verdictGroups = filteredVerdicts.reduce((acc, verdict) => {
    if (!acc[verdict.verdict]) {
      acc[verdict.verdict] = [];
    }
    acc[verdict.verdict].push(verdict);
    return acc;
  }, {});

  const handleVerdictClick = (verdictType, verdicts) => {
    if (isMobile) {
      setSelectedVerdict({ type: verdictType, verdicts });
      setDialogOpen(true);
    }
  };

  const verdictSize = isMobile ? 32 : 20;
  const countBadgeSize = isMobile ? 18 : 14;
  const emojiSize = isMobile ? '20px' : '12px';

  return (
    <>
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {Object.entries(verdictGroups).map(([verdictType, verdicts]) => {
          const config = verdictConfig[verdictType];
          const userNames = verdicts.map(v => v.user_nom).join(', ');
          const count = verdicts.length;

          const verdictElement = (
  <Box sx={{ position: 'relative', display: 'inline-block' }}>
    <Box
      onClick={() => handleVerdictClick(verdictType, verdicts)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        fontSize: emojiSize,
        '&:hover': isMobile ? {} : {
          transform: 'scale(1.1)',
          transition: 'transform 0.2s'
        }
      }}
    >
      {config.emoji}
    </Box>
    {count > 1 && (
      <Box
        sx={{
          position: 'absolute',
          top: -8,
          right: -8,
          backgroundColor: config.color,
          color: '#fff',
          borderRadius: '50%',
          width: countBadgeSize,
          height: countBadgeSize,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: isMobile ? 9 : 8,
          fontWeight: 'bold',
          border: `1px solid #fff`,
        }}
      >
        {count}
      </Box>
    )}
  </Box>
);

          if (isMobile) {
            return <Box key={verdictType}>{verdictElement}</Box>;
          } else {
            return (
              <Tooltip
                key={verdictType}
                title={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{config.label}</Typography>
                    <Typography variant="body2">{userNames}</Typography>
                  </Box>
                }
              >
                {verdictElement}
              </Tooltip>
            );
          }
        })}
      </Box>

      {/* Dialog pour mobile */}
      {isMobile && (
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
          <DialogTitle>{selectedVerdict?.type ? verdictConfig[selectedVerdict.type]?.label : ''}</DialogTitle>
          <DialogContent>
            <List>
              {selectedVerdict?.verdicts.map((verdict, index) => (
                <ListItem key={index}>
                  <ListItemText
                    primary={verdict.user_nom}
                    secondary={new Date(verdict.created_at).toLocaleDateString()}
                  />
                </ListItem>
              ))}
            </List>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

const PlayerResults = ({
  filteredPlayers,
  page,
  rowsPerPage,
  isMobile,
  handleViewPlayer,
  handleDeleteClick,
  userRole,
  selectedPlayerIDs,
  handleSelectPlayer,
  groupColors,
  mediaUrl,
  timestamp,
  allChecked,
  isIndeterminate,
  handleSelectAll,
  onPageChange,
  onRowsPerPageChange,
  currentUserId, // Ajout du currentUserId
}) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const isScout = userRole === 'scout interne' || userRole === 'scout externe' || userRole === 'scout interne cantera' || userRole === 'scout externe cantera';

  // Récupérer la couleur primaire du club depuis le contexte
  const { primaryColor: clubPrimaryColor } = useClubTheme();

  // Use the shared comparison context
  const {
    compareMode,
    playersToCompare,
    originalPlayers,
    effectivePlayers,
    togglePlayerComparison,
    isPlayerSelected,
    enterCompareMode,
    exitCompareMode,
    clearComparison,
    canCompare
  } = usePlayerComparison();

  // État pour le dialogue de comparaison
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  const totalCount = effectivePlayers.length;

  // Gérer l'ouverture du dialogue de comparaison
  const handleOpenCompareDialog = () => {
    setCompareDialogOpen(true);
    setSearchResults(originalPlayers);
  };

  // Fermer le dialogue
  const handleCloseCompareDialog = () => {
    setCompareDialogOpen(false);
    setSearchText('');
    setSearchResults(originalPlayers);
    // Effacer les joueurs sélectionnés lors de l'annulation
    clearComparison();
  };

  // Recherche en temps réel
  useEffect(() => {
    if (searchText.length < 3) {
      setSearchResults([]);
      return;
    }
    const lower = searchText.toLowerCase();
    const matches = originalPlayers.filter((p) =>
      p.nom.toLowerCase().includes(lower)
    );
    setSearchResults(matches);
  }, [searchText, originalPlayers]);


  // Valider la comparaison
  const handleValidateCompare = () => {
    if (playersToCompare.length < 2) return;
    enterCompareMode();
    setCompareDialogOpen(false);
  };

  const PlayerCard = ({ player }) => (
    <Card sx={{ mb: 2, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
      <CardHeader
        avatar={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar
              src={`${mediaUrl}/photos/${player.id}.png?${timestamp}`}
              alt={player.nom}
              sx={{ width: 60, height: 60 }}
            />
            <VerdictIndicators player={player} isMobile={true} userRole={userRole} currentUserId={currentUserId} />
          </Box>
        }
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography
                variant="h6"
                component="span"
                sx={{
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' },
                  color: theme.palette.primary.main
                }}
                onClick={() => handleViewPlayer(player)}
              >
                {player.nom}
              </Typography>
              {player.in_injury === 1 && (
                <HealingIcon
                  sx={{
                    color: 'red',
                    fontSize: 20,
                  }}
                />
              )}
              {player.in_tanteo === 1 && (
                <GavelIcon
                  sx={{
                    color: '#f9a825',
                    fontSize: 20,
                  }}
                />
              )}
            </Box>
          </Box>
        }
        subheader={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Chip 
              icon={<SportsIcon />}
              label={player.poste} 
              size="small" 
              sx={{ backgroundColor: '#e3f2fd', color: '#1976d2' }}
            />
            <FormaIndicator forma={player.forme_indicator} />
          </Box>
        }
        sx={{ pb: 1 }}
      />
      
      <CardContent sx={{ pt: 0, pb: 1 }}>
        {/* Informations de base */}
        <Grid container spacing={1} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <Typography variant="body2" color="textSecondary" sx={{ fontSize: '12px' }}>
                {t('playerResults.mobile.year')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '13px' }}>
                {player.anneeNaissance}
                {player.dateNaissance ? ` (${calculateDecimalAge(player.dateNaissance)})` : ''}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <HeightIcon sx={{ fontSize: 16, color: '#666' }} />
              <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '13px' }}>
                {formatHeight(player.taille, i18n.language)}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Nationalité et passeport */}
        {(player?.nacionalidad && player.nacionalidad !== 'null') && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <FlagDisplay nationality={player?.nacionalidad} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '13px' }}>
                {t(`createPlayer.nationalities.${player.nacionalidad}`, player.nacionalidad)}
              </Typography>
              {player?.pasaporte && player.pasaporte !== 'null' && (
                <Typography variant="body2" color="textSecondary" sx={{ fontSize: '11px' }}>
                  {t('playerResults.mobile.passport')} {t(`createPlayer.nationalities.${player.pasaporte}`, player.pasaporte)}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* Équipe et ligues */}
        <Box sx={{ mb: 2 }}>
          {!!player.is_free_agent && (
            <Box sx={{ mb: 1 }}>
              <Chip
                label={t('createPlayer.fields.freeAgentBadge')}
                size="small"
                sx={{
                  backgroundColor: '#4caf50',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '11px',
                  height: '22px'
                }}
              />
            </Box>
          )}
          {player.competitions_detail && player.competitions_detail.length > 0 ? (
            <>
              {/* Afficher les équipes et compétitions depuis competitions_detail */}
              {[...new Set(player.competitions_detail.map(c => c.equipe_nom))].map((equipe, idx) => {
                const equipeData = player.competitions_detail.find(c => c.equipe_nom === equipe);
                const competitions = player.competitions_detail
                  .filter(c => c.equipe_nom === equipe)
                  .filter((value, index, self) =>
                    index === self.findIndex((c) => c.competition_id === value.competition_id)
                  );

                return (
                  <Box key={idx} sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <TeamIcon sx={{ fontSize: 16, color: '#666' }} />
                      {/* Logo de l'équipe */}
                      <Box
                        component="img"
                        src={`${mediaUrl}/equipes/${equipeData.equipe_id}.jpg?t=${timestamp}`}
                        alt={equipe}
                        sx={{
                          width: 20,
                          height: 20,
                          objectFit: 'contain',
                          borderRadius: '2px'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '13px' }}>
                        {equipe}
                      </Typography>
                    </Box>
                    {/* Logos et noms des compétitions */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', ml: 2.5, mt: 0.5 }}>
                      {competitions.map((comp, compIdx) => (
                        <Box key={compIdx} sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <Box
                            component="img"
                            src={`${mediaUrl}/competitions/${comp.competition_id}.jpg?t=${timestamp}`}
                            alt={comp.competition_nom}
                            sx={{
                              width: 16,
                              height: 16,
                              objectFit: 'contain',
                              borderRadius: '2px'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                          <Typography variant="body2" color="textSecondary" sx={{ fontSize: '11px' }}>
                            {comp.competition_nom}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                );
              })}
            </>
          ) : (
            <>
              {/* Affichage classique pour compatibilité */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <TeamIcon sx={{ fontSize: 16, color: '#666' }} />
                <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '13px' }}>
                  {player.team || t('playerResults.mobile.noTeam')}
                </Typography>
              </Box>
              {player.league_1 && (
                <Typography variant="body2" color="textSecondary" sx={{ fontSize: '12px', ml: 2.5 }}>
                  {t('playerResults.mobile.league')} {player.league_1}
                </Typography>
              )}
              {player.league_2 && player.league_2 !== 'null' && (
                <Typography variant="body2" color="textSecondary" sx={{ fontSize: '12px', ml: 2.5 }}>
                  {player.league_2}
                </Typography>
              )}
            </>
          )}
        </Box>

        {/* Groupes */}
        {player.groupes && player.groupes[0]?.id !== null && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {player.groupes.map((group) => (
                <Chip
                  key={group.id}
                  label={group.libelle}
                  size="small"
                  sx={{
                    backgroundColor: group.code_couleur || 'grey',
                    color: getContrastColor(group.code_couleur),
                    fontSize: '10px',
                    height: '24px'
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Compteurs Informe/Scout */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <AssessmentIcon sx={{ fontSize: 16, color: '#666' }} />
            <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '12px' }}>
              {t('playerResults.mobile.informesScouts')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, ml: 2.5 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="textSecondary" sx={{ fontSize: '10px', display: 'block' }}>
                {t('playerResults.mobile.informe')}
              </Typography>
              <CountBadge count={player.nb_informe || 0} />
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="textSecondary" sx={{ fontSize: '10px', display: 'block' }}>
                {t('playerResults.mobile.scout')}
              </Typography>
              <CountBadge count={player.nb_scout || 0} />
            </Box>
          </Box>
        </Box>

        {/* Badges d'état (Admin seulement) */}
        {(userRole === 'admin pro' || userRole === 'admin cantera' || userRole === 'super admin') && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {player.in_big_board === 1 && (
                <Chip
                  icon={<StarIcon />}
                  label={t('playerResults.badges.ranking')}
                  size="small"
                  sx={{ backgroundColor: theme.palette.primary.main, color: '#fff' }}
                />
              )}
              {player.in_follow_list === 1 && (
                <Chip
                  icon={<VisibilityIcon />}
                  label={t('playerResults.badges.seguimiento')}
                  size="small"
                  sx={{ backgroundColor: theme.palette.secondary.main, color: '#fff' }}
                />
              )}
              {player.in_informe === 1 && (
                <Chip
                  icon={<AssignmentIcon />}
                  label={t('playerResults.badges.informe')}
                  size="small"
                  sx={{ backgroundColor: theme.palette.success.main, color: '#fff' }}
                />
              )}
              {player.in_intel === 1 && (
                <Chip
                  icon={<LightbulbIcon />}
                  label={t('playerResults.badges.intel')}
                  size="small"
                  sx={{ backgroundColor: theme.palette.warning.main, color: '#fff' }}
                />
              )}
              {player.in_fiche_technique === 1 && (
                <Chip
                  icon={<DescriptionIcon />}
                  label={t('playerResults.badges.fichaTecnica')}
                  size="small"
                  sx={{ backgroundColor: theme.palette.info.main, color: '#fff' }}
                />
              )}
              {player.in_video === 1 && (
                <Chip
                  icon={<VideoLibraryIcon />}
                  label={t('playerResults.badges.video')}
                  size="small"
                  sx={{ backgroundColor: "#9c27b0", color: '#fff' }}
                />
              )}
              {player.has_medidas === 1 && (
                <Chip
                  icon={<StraightenIcon />}
                  label={t('playerResults.badges.medidas')}
                  size="small"
                  sx={{ backgroundColor: "#00bcd4", color: '#fff' }}
                />
              )}
              {player.has_documents === 1 && (
                <Chip
                  icon={<FolderOpenIcon />}
                  label={t('playerResults.badges.documents')}
                  size="small"
                  sx={{ backgroundColor: "#795548", color: '#fff' }}
                />
              )}
            </Box>
          </Box>
        )}
      </CardContent>
      
      <Divider />
      
      <CardActions sx={{ justifyContent: 'space-between', px: 2, py: 1 }}>
        <Button
          size="small"
          startIcon={<Visibility />}
          onClick={() => handleViewPlayer(player)}
          sx={{
            color: theme.palette.primary.main,
            fontSize: '12px',
            textTransform: 'none'
          }}
        >
          {t('playerResults.buttons.viewDetails')}
        </Button>
        {!isScout && (
          <Button
            size="small"
            startIcon={<DeleteIcon />}
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClick(player, e);
            }}
            sx={{
              color: theme.palette.error.main,
              fontSize: '12px',
              textTransform: 'none'
            }}
          >
            {t('playerResults.buttons.delete')}
          </Button>
        )}
      </CardActions>
    </Card>
  );

  if (isMobile) {
    return (
      <Box>
        {/* Header avec total et bouton comparar */}
        <Box sx={{ 
          mb: 2, 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2
        }}>
          <Box sx={{ 
            p: 2, 
            backgroundColor: clubPrimaryColor, 
            borderRadius: 2,
            textAlign: 'center',
            flex: 1
          }}>
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
              {t('playerResults.counter.total')} {totalCount} {totalCount !== 1 ? t('playerResults.counter.playerPlural') : t('playerResults.counter.playerSingular')}
            </Typography>
          </Box>
          {!compareMode && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenCompareDialog}
              sx={{ height: 'fit-content' }}
            >
              {t('playerResults.buttons.compare')}
            </Button>
          )}
          {compareMode && (
            <Button
              variant="outlined"
              color="primary"
              onClick={exitCompareMode}
              sx={{ height: 'fit-content' }}
            >
              {t('playerResults.buttons.back')}
            </Button>
          )}
        </Box>

        {/* Lista de jugadores */}
        {effectivePlayers
          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
          .map((player) => (
            <PlayerCard
              key={player.id}
              player={player}
            />
          ))}
        
        {/* Paginación */}
        <TablePagination
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage={t('playerResults.pagination.perPage')}
          labelDisplayedRows={({ from, to, count }) => t('playerResults.pagination.displayedRows', { from, to, count })}
          sx={{
            '& .MuiTablePagination-toolbar': {
              fontSize: '14px'
            },
            '& .MuiTablePagination-selectLabel': {
              fontSize: '12px'
            },
            '& .MuiTablePagination-displayedRows': {
              fontSize: '12px'
            }
          }}
        />

        {/* Dialogue de comparaison */}
        <Dialog open={compareDialogOpen} onClose={handleCloseCompareDialog} maxWidth="sm" fullWidth>
          <DialogTitle>{t('playerResults.comparison.title')}</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              placeholder={t('playerResults.comparison.searchPlaceholder')}
              variant="outlined"
              size="small"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              sx={{
                mt: 1,
                mb: 2
              }}
            />
            {/* Affichage des joueurs sélectionnés */}
            {playersToCompare.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                  {playersToCompare.length === 1
                    ? `1 ${t('playerResults.comparison.selectedSingular')}`
                    : `${playersToCompare.length} ${t('playerResults.comparison.selectedPlural')}`}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {playersToCompare.map(player => (
                    <Chip
                      key={player.id}
                      label={player.nom || player.name || `Jugador ${player.id}`}
                      onDelete={() => togglePlayerComparison(player)}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            )}
            <List dense sx={{ maxHeight: 400, overflow: 'auto' }}>
              {searchResults.map((player) => {
                const labelId = `checkbox-list-label-${player.id}`;
                const checked = isPlayerSelected(player.id);

                return (
                  <ListItem
                    key={player.id}
                    button
                    onClick={() => togglePlayerComparison(player)}
                    secondaryAction={<Checkbox edge="end" checked={checked} />}
                  >
                    <ListItemAvatar>
                      <Avatar
                        src={`${mediaUrl}/photos/${player.id}.png?${timestamp}`}
                        alt={player.nom}
                      />
                    </ListItemAvatar>
                    <ListItemText id={labelId} primary={player.nom} />
                  </ListItem>
                );
              })}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCompareDialog}>{t('playerResults.buttons.cancel')}</Button>
            <Button
              variant="contained"
              onClick={handleValidateCompare}
              disabled={playersToCompare.length < 2}
            >
              {t('playerResults.buttons.validate')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header avec bouton comparar */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        {!compareMode && (
          <Button variant="contained" color="primary" onClick={handleOpenCompareDialog}>
            {t('playerResults.buttons.compare')}
          </Button>
        )}
        {compareMode && (
          <>
            <Button variant="outlined" color="primary" onClick={exitCompareMode}>
              {t('playerResults.buttons.back')}
            </Button>
            <Typography variant="body1" sx={{ ml: 2 }}>
              {t('playerResults.comparison.comparing', { count: effectivePlayers.length })}
            </Typography>
          </>
        )}
      </Box>

      <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'hidden' }}>
        <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
          <TableHead>
            <TableRow>
              {userRole === 'super admin' && (
                <TableCell 
                  sx={{ 
                    backgroundColor: clubPrimaryColor, 
                    color: 'white', 
                    fontWeight: 'bold',
                    textAlign: 'center',
                    width: '50px',
                    padding: '8px 4px'
                  }}
                >
                  <Checkbox
                    checked={allChecked}
                    indeterminate={isIndeterminate}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    sx={{ color: 'white' }}
                  />
                </TableCell>
              )}
              <TableCell sx={{ backgroundColor: clubPrimaryColor, color: 'white', fontWeight: 'bold', textAlign: 'center', width: '80px', padding: '8px 4px' }}>
                <Box sx={{
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  borderRadius: '4px',
                  padding: '2px 6px',
                  border: '1px solid rgba(255,255,255,0.3)',
                  display: 'inline-block'
                }}>
                  <span style={{ fontSize: '12px', fontWeight: 'bold' }}>{totalCount}</span>
                  <span style={{ fontSize: '9px', opacity: 0.8, marginLeft: '2px' }}>{t('playerResults.table.players')}</span>
                </Box>
              </TableCell>
              <TableCell sx={{ backgroundColor: clubPrimaryColor, color: 'white', fontWeight: 'bold', textAlign: 'left', paddingLeft: '8px', width: '140px', padding: '8px 4px' }}>
                {t('playerResults.table.name')}
              </TableCell>
              <TableCell sx={{ backgroundColor: clubPrimaryColor, color: 'white', fontWeight: 'bold', textAlign: 'left', paddingLeft: '8px', width: '50px', padding: '8px 4px' }}>
                {t('playerResults.table.form')}
              </TableCell>
              <TableCell sx={{ backgroundColor: clubPrimaryColor, color: 'white', fontWeight: 'bold', textAlign: 'left', paddingLeft: '8px', width: '80px', padding: '8px 4px' }}>
                {t('playerResults.table.position')}
              </TableCell>
              <TableCell sx={{ backgroundColor: clubPrimaryColor, color: 'white', fontWeight: 'bold', textAlign: 'left', paddingLeft: '8px', width: '100px', padding: '8px 4px' }}>
                {t('playerResults.table.year')}
              </TableCell>
              <TableCell sx={{ backgroundColor: clubPrimaryColor, color: 'white', fontWeight: 'bold', textAlign: 'left', paddingLeft: '8px', width: '50px', padding: '8px 4px' }}>
                {t('playerResults.table.height')}
              </TableCell>
              <TableCell sx={{ backgroundColor: clubPrimaryColor, color: 'white', fontWeight: 'bold', textAlign: 'center', width: '80px', padding: '8px 4px' }}>
                {t('playerResults.table.nationality')}
              </TableCell>
              <TableCell sx={{ backgroundColor: clubPrimaryColor, color: 'white', fontWeight: 'bold', textAlign: 'center', width: '120px', padding: '8px 4px' }}>
                {t('playerResults.table.team')}
              </TableCell>
              <TableCell sx={{ backgroundColor: clubPrimaryColor, color: 'white', fontWeight: 'bold', textAlign: 'left', paddingLeft: '8px', width: '80px', padding: '8px 4px' }}>
                {t('playerResults.table.groups')}
              </TableCell>
              <TableCell sx={{ backgroundColor: clubPrimaryColor, color: 'white', fontWeight: 'bold', textAlign: 'center', width: '96px', padding: '8px 8px', lineHeight: 1.2 }}>
                {t('playerResults.table.informeScout')}
              </TableCell>
              <TableCell sx={{ backgroundColor: clubPrimaryColor, color: 'white', fontWeight: 'bold', textAlign: 'center', width: '90px', padding: '8px 8px' }}>
                {t('playerResults.table.actions')}
              </TableCell>
              {(userRole === 'admin pro' ||
                userRole === 'admin cantera' ||
                userRole === 'super admin') && (
                <TableCell sx={{ backgroundColor: clubPrimaryColor, color: 'white', fontWeight: 'bold', textAlign: 'center', width: '120px', padding: '8px 4px' }}>
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {effectivePlayers
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((player) => {
                const isSelected = selectedPlayerIDs.includes(player.id);
                return (
                  <TableRow key={player.id} hover>
                    {userRole === 'super admin' && (
                      <TableCell sx={{ textAlign: 'center', padding: '4px' }}>
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handleSelectPlayer(player.id)}
                        />
                      </TableCell>
                    )}
                    <TableCell sx={{ textAlign: 'left', padding: '4px', paddingLeft: '8px' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', marginTop: '15px' }}>
                          <FlagDisplay nationality={player?.nacionalidad} />
                        </Box>
                        <Avatar
                          src={`${mediaUrl}/photos/${player.id}.png?${timestamp}`}
                          alt={player.nom}
                          sx={{ width: 36, height: 36 }}
                        />
                        <VerdictIndicators player={player} isMobile={false} userRole={userRole} currentUserId={currentUserId} />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'left', padding: '4px', paddingLeft: '8px' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography
                          component="span"
                          sx={{
                            cursor: 'pointer',
                            '&:hover': { textDecoration: 'underline' },
                            fontSize: '13px',
                            whiteSpace: 'nowrap'
                          }}
                          onClick={() => handleViewPlayer(player)}
                        >
                          {player.nom}
                        </Typography>
                        {player.in_injury === 1 && (
                          <HealingIcon
                            sx={{
                              color: 'red',
                              fontSize: 16,
                            }}
                          />
                        )}
                        {player.in_tanteo === 1 && (
                          <GavelIcon
                            sx={{
                              color: '#f9a825',
                              fontSize: 16,
                            }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'left', padding: '4px', paddingLeft: '8px' }}>
                      <FormaIndicator forma={player.forme_indicator} />
                    </TableCell>
                    <TableCell sx={{ textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '13px', padding: '4px', paddingLeft: '8px' }}>
                      {player.poste}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '12px', padding: '4px', paddingLeft: '8px' }}>
                      {player.anneeNaissance}
                      {player.dateNaissance ? ` (${calculateDecimalAge(player.dateNaissance)})` : ''}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '12px', padding: '4px', paddingLeft: '8px' }}>
                      {formatHeight(player.taille, i18n.language)}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '13px', padding: '4px' }}>
                      <Box>
                        <div>{player?.nacionalidad && player.nacionalidad !== 'null' ? t(`createPlayer.nationalities.${player.nacionalidad}`, player.nacionalidad) : ''}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {player?.pasaporte && player.pasaporte !== 'null' ? t(`createPlayer.nationalities.${player.pasaporte}`, player.pasaporte) : ''}
                        </div>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '13px', padding: '4px' }}>
                      <Box>
                        {!!player.is_free_agent && (
                          <Chip
                            label={t('createPlayer.fields.freeAgentBadge')}
                            size="small"
                            sx={{
                              backgroundColor: '#4caf50',
                              color: 'white',
                              fontWeight: 'bold',
                              fontSize: '10px',
                              height: '20px',
                              mb: 0.5
                            }}
                          />
                        )}
                        {/* Si on a des données dans competitions_detail, les afficher en priorité */}
                        {player.competitions_detail && player.competitions_detail.length > 0 ? (
                          <>
                            {/* Grouper par équipe */}
                            {[...new Set(player.competitions_detail.map(c => c.equipe_nom))].map((equipe, idx) => {
                              // Récupérer l'ID de l'équipe et les compétitions associées
                              const equipeData = player.competitions_detail.find(c => c.equipe_nom === equipe);
                              const competitions = player.competitions_detail
                                .filter(c => c.equipe_nom === equipe)
                                .filter((value, index, self) =>
                                  index === self.findIndex((c) => c.competition_id === value.competition_id)
                                );

                              return (
                                <Box key={idx} sx={{ mb: 0.5 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                    {/* Logo de l'équipe */}
                                    <Box
                                      component="img"
                                      src={`${mediaUrl}/equipes/${equipeData.equipe_id}.jpg?t=${timestamp}`}
                                      alt={equipe}
                                      sx={{
                                        width: 16,
                                        height: 16,
                                        objectFit: 'contain',
                                        borderRadius: '2px'
                                      }}
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                      }}
                                    />
                                    <div style={{ fontWeight: 'bold', fontSize: '12px' }}>{equipe}</div>
                                  </Box>
                                  {/* Logos et noms des compétitions */}
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.3, flexWrap: 'wrap', mt: 0.3 }}>
                                    {competitions.map((comp, compIdx) => (
                                      <Box key={compIdx} sx={{ display: 'flex', alignItems: 'center', gap: 0.2 }}>
                                        <Box
                                          component="img"
                                          src={`${mediaUrl}/competitions/${comp.competition_id}.jpg?t=${timestamp}`}
                                          alt={comp.competition_nom}
                                          sx={{
                                            width: 14,
                                            height: 14,
                                            objectFit: 'contain',
                                            borderRadius: '2px'
                                          }}
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                          }}
                                        />
                                        <span style={{ fontSize: '10px', color: '#666' }}>{comp.competition_nom}</span>
                                      </Box>
                                    ))}
                                  </Box>
                                </Box>
                              );
                            })}
                          </>
                        ) : (
                          <>
                            {/* Afficher les anciennes données pour compatibilité */}
                            <div>{player.team}</div>
                            <div style={{ fontSize: '12px', color: '#666' }}>{player.league_1}</div>
                            {player.league_2 && player.league_2 !== 'null' && (
                              <div style={{ fontSize: '11px', color: '#888' }}>{player.league_2}</div>
                            )}
                          </>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'left', padding: '4px', paddingLeft: '8px' }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25 }}>
                        {player.groupes &&
                          player.groupes[0]?.id !== null &&
                          player.groupes.map((group) => (
                            <Chip
                              key={group.id}
                              label={group.libelle}
                              size="small"
                              sx={{
                                backgroundColor: group.code_couleur || 'grey',
                                color: getContrastColor(group.code_couleur),
                                fontSize: '10px',
                                height: '20px'
                              }}
                            />
                          ))}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center', padding: '4px' }}>
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', justifyContent: 'center' }}>
                        <CountBadge count={player.nb_informe || 0} />
                        <CountBadge count={player.nb_scout || 0} />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center', padding: '4px' }}>
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        <IconButton 
                          size="small"
                          onClick={() => handleViewPlayer(player)} 
                          sx={{ color: '#ff9800' }}
                        >
                          <Visibility />
                        </IconButton>
                        {!isScout && (
                          <IconButton
                            size="small"
                            onClick={(e) => handleDeleteClick(player, e)}
                            sx={{ color: '#f44336' }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                    {(userRole === 'admin pro' ||
                      userRole === 'admin cantera' ||
                      userRole === 'super admin') && (
                      <TableCell sx={{ textAlign: 'left', padding: '4px', paddingLeft: '8px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.25 }}>
                          {player.in_big_board === 1 && (
                            <Tooltip title={t('playerResults.badges.ranking')}>
                              <Box sx={badgeStyle(theme.palette.primary.main)}>
                                <StarIcon fontSize="inherit" />
                              </Box>
                            </Tooltip>
                          )}
                          {player.in_follow_list === 1 && (
                            <Tooltip title={t('playerResults.badges.seguimiento')}>
                              <Box sx={badgeStyle(theme.palette.secondary.main)}>
                                <VisibilityIcon fontSize="inherit" />
                              </Box>
                            </Tooltip>
                          )}
                          {player.in_informe === 1 && (
                            <Tooltip title={t('playerResults.badges.informe')}>
                              <Box sx={badgeStyle(theme.palette.success.main)}>
                                <AssignmentIcon fontSize="inherit" />
                              </Box>
                            </Tooltip>
                          )}
                          {player.in_intel === 1 && (
                            <Tooltip title={t('playerResults.badges.intel')}>
                              <Box sx={badgeStyle(theme.palette.warning.main)}>
                                <LightbulbIcon fontSize="inherit" />
                              </Box>
                            </Tooltip>
                          )}
                          {player.in_fiche_technique === 1 && (
                            <Tooltip title={t('playerResults.badges.fichaTecnica')}>
                              <Box sx={badgeStyle(theme.palette.info.main)}>
                                <DescriptionIcon fontSize="inherit" />
                              </Box>
                            </Tooltip>
                          )}
                          {player.in_video === 1 && (
                            <Tooltip title={t('playerResults.badges.video')}>
                              <Box sx={badgeStyle("#9c27b0")}>
                                <VideoLibraryIcon fontSize="inherit" />
                              </Box>
                            </Tooltip>
                          )}
                          {player.has_medidas === 1 && (
                            <Tooltip title={t('playerResults.badges.medidas')}>
                              <Box sx={badgeStyle("#00bcd4")}>
                                <StraightenIcon fontSize="inherit" />
                              </Box>
                            </Tooltip>
                          )}
                          {player.has_documents === 1 && (
                            <Tooltip title={t('playerResults.badges.documents')}>
                              <Box sx={badgeStyle("#795548")}>
                                <FolderOpenIcon fontSize="inherit" />
                              </Box>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[10, 25, 50, 100]}
        labelRowsPerPage=""
        labelDisplayedRows={({ from, to, count }) => t('playerResults.pagination.displayedRows', { from, to, count })}
      />

      {/* Dialogue de comparaison */}
      <Dialog open={compareDialogOpen} onClose={handleCloseCompareDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t('playerResults.comparison.title')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t('playerResults.comparison.searchPlaceholder')}
            variant="outlined"
            size="small"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{ mb: 2 }}
          />
          {/* Affichage des joueurs sélectionnés */}
          {playersToCompare.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                {playersToCompare.length === 1
                  ? `1 ${t('playerResults.comparison.selectedSingular')}`
                  : `${playersToCompare.length} ${t('playerResults.comparison.selectedPlural')}`}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {playersToCompare.map(player => (
                  <Chip
                    key={player.id}
                    label={player.nom || player.name || `Jugador ${player.id}`}
                    onDelete={() => togglePlayerComparison(player)}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}
          <List dense sx={{ maxHeight: 400, overflow: 'auto' }}>
            {searchResults.map((player) => {
              const labelId = `checkbox-list-label-${player.id}`;
              const checked = isPlayerSelected(player.id);

              return (
                <ListItem
                  key={player.id}
                  button
                  onClick={() => togglePlayerComparison(player)}
                  secondaryAction={<Checkbox edge="end" checked={checked} />}
                >
                  <ListItemAvatar>
                    <Avatar
                      src={`${mediaUrl}/photos/${player.id}.png?${timestamp}`}
                      alt={player.nom}
                    />
                  </ListItemAvatar>
                  <ListItemText id={labelId} primary={player.nom} />
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCompareDialog}>{t('playerResults.buttons.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleValidateCompare}
            disabled={playersToCompare.length < 2}
          >
            {t('playerResults.buttons.validate')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlayerResults;